import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, alpha, Autocomplete, Box, Button, CircularProgress, IconButton, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import ETextField from '@ui/pages/EventRegistration/RegistrationForm/components/ETextField';
import EDatePicker from '@ui/pages/EventRegistration/RegistrationForm/components/EDatePicker';
import ESelect from '@ui/pages/EventRegistration/RegistrationForm/components/ESelect';
import EButtonGroupField from '@ui/pages/EventRegistration/RegistrationForm/components/EButtonGroupField';
import ERadioGroup from '@ui/pages/EventRegistration/RegistrationForm/components/ERadioGroup';
import ESwitch from '@ui/pages/EventRegistration/RegistrationForm/components/ESwitch';
import EAutocomplete from '@ui/pages/EventRegistration/RegistrationForm/components/EAutocomplete';
import { PickupLocationOptionRow } from '@ui/components/pickup-location/PickupLocationOptionRow';
import { filterPickupLocationOptions, getPickupLocationInputLabel } from '@application/utilities/pickup-location-utils';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import { isPhoneValidForCountry } from '@ui/components/PhoneMasking/phone-format';
import { ErrorWithIcon } from '@ui/components/common/TextField/ErrorWithIcon';
import { FamilyFestAttendanceStatusEnum } from '@application/enums/family-fest-registration-enums';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import pickupLocationAPI from '@infrastructure/pickup-location-api';
import { pdokSuggest, pdokLookup, parsePdokCoordinates } from '@infrastructure/pdok-service';
export const COMPANY_OPTIONS = [
    { label: 'VDK Group', value: 'vdk-group' },
    { label: 'Phantasialand', value: 'phantasialand' },
    { label: 'Guest', value: 'guest' }
];
export const getFamilyRelationOptions = (t) => [
    { label: t('Relation.Partner'), value: 'partner' },
    { label: t('Relation.Child'), value: 'kind' },
    { label: t('Relation.GrandChild'), value: 'grandchild' },
    // { label: t('Relation.FamilyMember'), value: 'familie' },
    // { label: t('Relation.Other'), value: 'other' }
];
export const getGuestRelationOptions = (t) => [
    { label: t('Relation.Guest'), value: 'guest' }
];
export const getAirportOptions = (t) => [
    { label: t('Airport.Goteborg'), value: 'goteborg' },
    { label: t('Airport.Malmo'), value: 'malmo' },
    { label: t('Airport.Stockholm'), value: 'stockholm' }
];
const ADDRESS_AUTOCOMPLETE_MIN_LENGTH = 2;
const ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS = 350;
export const EMPTY_PERSON = {
    firstName: '',
    middleName: '',
    lastName: '',
    relationshipToYou: '',
    company: '',
    email: '',
    eventChoice: '',
    attendanceStatus: '',
    supportComments: '',
    reasonForCancellation: '',
    postalcode: '',
    houseNumber: '',
    addition: '',
    Address: '',
    addressCountry: 'nl',
    addressPlaceId: '',
    addressLatitude: null,
    addressLongitude: null,
    Phone: '',
    PhoneCountryCode: 'nl',
    PhoneDialCode: '31',
    DateOfBirth: null,
    TravelMode: '',
    vehicleCount: 1,
    bookingInfoRequired: '',
    bookingInfo: '',
    Option: '',
    ComingWith: '',
    sameAddress: false,
    accommodationRequired: '',
    TermsAccepted: []
};
const normalized = (value) => String(value ?? '')
    .toLowerCase()
    .trim();
const personField = (index, field) => `persons.${index}.${field}`;
export const FamilyPersonCard = ({ index, displayNumber, control, errors, persons, isRegistered, primaryComingWith, showStepTwoFields = false, isAdditionalPerson = false, totalCount, onRemove, setValue, companyOptions, pickupLocationOptions, isEditMode = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const companyFieldOptions = companyOptions?.length ? companyOptions : COMPANY_OPTIONS;
    const transportFieldOptions = pickupLocationOptions ?? [];
    const fieldError = (field) => errors?.persons?.[index]?.[field]?.message;
    const p = persons?.[index] ?? {};
    const eventChoice = p.eventChoice;
    const selectedOption = p.Option;
    const bookingInfoChoice = p.bookingInfoRequired;
    const comingWith = normalized(p.ComingWith);
    const attendanceStatus = p.attendanceStatus;
    const isNotAttending = normalized(eventChoice) === 'false';
    const travelMode = normalized(p.TravelMode);
    const transportLocation = p.transportLocation;
    const selectedTransport = transportFieldOptions.find((opt) => opt.value === transportLocation);
    const locationHasAccommodation = selectedTransport?.hasAccommodation ?? !!p.isPickupAccommodationAvailable;
    const sameAddress = !!p.sameAddress;
    const effectiveComingWith = isAdditionalPerson ? normalized(primaryComingWith) : comingWith;
    const hideCompany = (showStepTwoFields || isAdditionalPerson) &&
        (effectiveComingWith === 'family' ||
            effectiveComingWith === 'guest' ||
            effectiveComingWith === 'introduction');
    const showHeader = totalCount > 1 && comingWith !== 'alone' && isAdditionalPerson;
    const f = (field) => personField(index, field);
    const relationOptions = effectiveComingWith === 'guest' || effectiveComingWith === 'introduction'
        ? getGuestRelationOptions(t)
        : getFamilyRelationOptions(t);
    const personName = [p.firstName, p.middleName, p.lastName]
        .filter((x) => String(x ?? '').trim().length > 0)
        .join(' ');
    const effectiveEventChoice = isAdditionalPerson ? 'true' : eventChoice;
    // const showHotelBookingQuestion =
    //   showPerPersonBooking &&
    //   (travelMode === 'auto' ||
    //     (travelMode === 'bus' &&
    //       locationHasAccommodation &&
    //       normalized(p.accommodationRequired) === 'false'));
    const showHotelBookingQuestion = false;
    const bgColor = theme.palette.common.grey8;
    const [sessionToken, setSessionToken] = useState(uuidv4());
    const [addressInput, setAddressInput] = useState('');
    const [addressOptions, setAddressOptions] = useState([]);
    const [isAddressSuggestionsLoading, setIsAddressSuggestionsLoading] = useState(false);
    const [isPlaceDetailsLoading, setIsPlaceDetailsLoading] = useState(false);
    const [postalCodeInput, setPostalCodeInput] = useState('');
    const [postalCodeOptions, setPostalCodeOptions] = useState([]);
    const [isPostalCodeSuggestionsLoading, setIsPostalCodeSuggestionsLoading] = useState(false);
    const [isPostalCodeDetailsLoading, setIsPostalCodeDetailsLoading] = useState(false);
    const [flagAnchorEl, setFlagAnchorEl] = useState(null);
    const flagOpen = Boolean(flagAnchorEl);
    const selectedAddressCountry = normalized(p.addressCountry || 'nl');
    const isSwedenCountry = selectedAddressCountry === 'se';
    const hasSelectedDateOfBirth = !!p.DateOfBirth;
    const airportOptions = useMemo(() => getAirportOptions(t), [t]);
    const isAgeAtLeast14 = useMemo(() => {
        if (!p.DateOfBirth)
            return false;
        const dob = new Date(p.DateOfBirth);
        if (isNaN(dob.getTime()))
            return false;
        const today = new Date();
        const age = today.getFullYear() -
            dob.getFullYear() -
            (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
        return age >= 14;
    }, [p.DateOfBirth]);
    // Reset DOB when Sweden is selected and existing DOB makes person under 14.
    useEffect(() => {
        if (!isSwedenCountry || !p.DateOfBirth || !setValue)
            return;
        const dob = new Date(p.DateOfBirth);
        if (isNaN(dob.getTime()))
            return;
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - 14);
        if (dob > cutoff) {
            setValue(f('DateOfBirth'), null, { shouldValidate: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSwedenCountry]);
    const [initialCountry, setInitialCountry] = useState(null);
    useEffect(() => {
        if (isEditMode && initialCountry === null && p.id) {
            setInitialCountry(normalized(p.addressCountry || 'nl'));
        }
    }, [p.id, p.addressCountry]);
    const swedenWasInitialCountry = initialCountry === 'se';
    const ADDRESS_FLAGS = [
        { code: 'nl', label: 'NL', icon: '/assets/images/flags/nl-flag.svg', disabled: false },
        { code: 'de', label: 'DE', icon: '/assets/images/flags/de-flag.svg', disabled: false },
        { code: 'be', label: 'BE', icon: '/assets/images/flags/be-flag.svg', disabled: false },
        { code: 'at', label: 'AT', icon: '/assets/images/flags/at-flag.svg', disabled: false },
        { code: 'ch', label: 'CH', icon: '/assets/images/flags/ch-flag.svg', disabled: false },
        { code: 'fr', label: 'FR', icon: '/assets/images/flags/fr-flag.svg', disabled: false },
        ...(isEditMode
            ? [
                {
                    code: 'se',
                    label: 'SE',
                    icon: '/assets/images/flags/se-flag.svg',
                    disabled: !swedenWasInitialCountry
                }
            ]
            : [])
    ];
    const currentFlag = ADDRESS_FLAGS.find((flag) => flag.code === selectedAddressCountry) ?? ADDRESS_FLAGS[0];
    const attendanceDecisionOptions = [
        {
            label: t('familyForm.yes') || 'Yes',
            value: 'true'
        },
        {
            label: t('familyForm.no') || 'No',
            value: 'false'
        }
    ];
    const notAttendingOptions = [
        {
            label: t('AttendanceStatus.CantMakeIt') || "Can't make it",
            value: String(FamilyFestAttendanceStatusEnum.CantMakeIt)
        },
        {
            label: t('AttendanceStatus.NoInterest') || 'No interest',
            value: String(FamilyFestAttendanceStatusEnum.NoInterest)
        },
        {
            label: t('AttendanceStatus.Others') || 'Others',
            value: String(FamilyFestAttendanceStatusEnum.Others)
        }
    ];
    // Sync TravelMode & transportLocation from primary person for additional persons
    useEffect(() => {
        if (!isAdditionalPerson || index === 0 || !setValue)
            return;
        const primaryTravelMode = persons?.[0]?.TravelMode;
        if (primaryTravelMode && !p.TravelMode) {
            setValue(f('TravelMode'), primaryTravelMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdditionalPerson, index]);
    useEffect(() => {
        if (!setValue)
            return;
        const shouldProvideAccommodationAutomatically = travelMode === 'bus' && !!transportLocation && locationHasAccommodation;
        setValue(personField(index, 'accommodationRequired'), shouldProvideAccommodationAutomatically ? 'true' : '', {
            shouldValidate: false,
            shouldDirty: true
        });
    }, [index, locationHasAccommodation, setValue, transportLocation, travelMode]);
    useEffect(() => {
        if (!setValue || showHotelBookingQuestion)
            return;
        setValue(personField(index, 'bookingInfoRequired'), '', {
            shouldValidate: false,
            shouldDirty: true
        });
        setValue(personField(index, 'bookingInfo'), '', {
            shouldValidate: false,
            shouldDirty: true
        });
    }, [index, setValue, showHotelBookingQuestion]);
    useEffect(() => {
        if (!setValue || !effectiveComingWith)
            return;
        if (effectiveComingWith === 'guest' || effectiveComingWith === 'introduction') {
            setValue(personField(index, 'relationshipToYou'), 'guest', {
                shouldValidate: true,
                shouldDirty: true
            });
        }
        else {
            setValue(personField(index, 'relationshipToYou'), p.relationshipToYou !== '' ? p.relationshipToYou : undefined, {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    }, [index, setValue, effectiveComingWith]);
    const prevSameAddressRef = useRef(sameAddress);
    useEffect(() => {
        if (!setValue || !isAdditionalPerson || index <= 0)
            return;
        if (sameAddress) {
            const primary = persons?.[0];
            if (primary) {
                setValue(personField(index, 'postalcode'), String(primary.postalcode ?? ''), {
                    shouldDirty: true,
                    shouldValidate: true
                });
                setValue(personField(index, 'houseNumber'), String(primary.houseNumber ?? ''), {
                    shouldDirty: true,
                    shouldValidate: true
                });
                setValue(personField(index, 'addition'), String(primary.addition ?? ''), {
                    shouldDirty: true,
                    shouldValidate: true
                });
                setValue(personField(index, 'Address'), String(primary.Address ?? ''), {
                    shouldDirty: true,
                    shouldValidate: true
                });
                const newCountry = String(primary.addressCountry ?? 'nl');
                const currentCountry = String(p.addressCountry ?? 'nl');
                setValue(personField(index, 'addressCountry'), newCountry, {
                    shouldDirty: true,
                    shouldValidate: true
                });
                if (newCountry !== currentCountry) {
                    setValue(personField(index, 'Phone'), '', { shouldDirty: true, shouldValidate: false });
                    setValue(personField(index, 'PhoneCountryCode'), newCountry, {
                        shouldDirty: true,
                        shouldValidate: false
                    });
                    setValue(personField(index, 'PhoneDialCode'), '', {
                        shouldDirty: true,
                        shouldValidate: false
                    });
                }
                setValue(personField(index, 'addressPlaceId'), String(primary.addressPlaceId ?? ''), {
                    shouldDirty: true,
                    shouldValidate: true
                });
                setValue(personField(index, 'addressLatitude'), primary.addressLatitude == null ? null : Number(primary.addressLatitude), {
                    shouldDirty: true,
                    shouldValidate: true
                });
                setValue(personField(index, 'addressLongitude'), primary.addressLongitude == null ? null : Number(primary.addressLongitude), {
                    shouldDirty: true,
                    shouldValidate: true
                });
            }
        }
        else if (prevSameAddressRef.current) {
            setValue(personField(index, 'postalcode'), '', { shouldDirty: true, shouldValidate: true });
            setValue(personField(index, 'houseNumber'), '', { shouldDirty: true, shouldValidate: true });
            setValue(personField(index, 'addition'), '', { shouldDirty: true, shouldValidate: true });
            setValue(personField(index, 'Address'), '', { shouldDirty: true, shouldValidate: true });
            setValue(personField(index, 'addressPlaceId'), '', {
                shouldDirty: true,
                shouldValidate: true
            });
            setValue(personField(index, 'addressLatitude'), null, {
                shouldDirty: true,
                shouldValidate: true
            });
            setValue(personField(index, 'addressLongitude'), null, {
                shouldDirty: true,
                shouldValidate: true
            });
        }
        prevSameAddressRef.current = sameAddress;
    }, [sameAddress, isAdditionalPerson, index, persons, setValue]);
    useEffect(() => {
        if (addressInput || !p.Address)
            return;
        setAddressInput(String(p.Address));
    }, [addressInput, p.Address]);
    useEffect(() => {
        const pc = String(p.postalcode ?? '');
        if (pc && postalCodeInput !== pc && !postalCodeOptions.length) {
            setPostalCodeInput(pc);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [p.postalcode]);
    useEffect(() => {
        if (sameAddress || isRegistered)
            return;
        const searchTerm = postalCodeInput.trim();
        if (searchTerm.length < ADDRESS_AUTOCOMPLETE_MIN_LENGTH) {
            setPostalCodeOptions([]);
            setIsPostalCodeSuggestionsLoading(false);
            return;
        }
        const timeout = window.setTimeout(async () => {
            try {
                setIsPostalCodeSuggestionsLoading(true);
                if (selectedAddressCountry === 'nl') {
                    const docs = await pdokSuggest(searchTerm);
                    setPostalCodeOptions(docs.map((doc) => ({ label: doc.weergavenaam, value: doc.id })));
                }
                else {
                    const response = await pickupLocationAPI.getPlacesAutocomplete({
                        input: searchTerm,
                        country: selectedAddressCountry,
                        sessiontoken: sessionToken,
                        language: 'en'
                    });
                    const predictions = response.data?.predictions ?? [];
                    setPostalCodeOptions(predictions.map((prediction) => ({
                        label: prediction.description,
                        value: prediction.place_id
                    })));
                }
            }
            catch {
                setPostalCodeOptions([]);
            }
            finally {
                setIsPostalCodeSuggestionsLoading(false);
            }
        }, ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS);
        return () => window.clearTimeout(timeout);
    }, [postalCodeInput, isRegistered, sameAddress, selectedAddressCountry, sessionToken]);
    useEffect(() => {
        if (sameAddress || isRegistered)
            return;
        const searchTerm = addressInput.trim();
        if (searchTerm.length < ADDRESS_AUTOCOMPLETE_MIN_LENGTH) {
            setAddressOptions([]);
            setIsAddressSuggestionsLoading(false);
            return;
        }
        const timeout = window.setTimeout(async () => {
            try {
                setIsAddressSuggestionsLoading(true);
                if (selectedAddressCountry === 'nl') {
                    const docs = await pdokSuggest(searchTerm);
                    setAddressOptions(docs.map((doc) => ({ label: doc.weergavenaam, value: doc.id })));
                }
                else {
                    const response = await pickupLocationAPI.getPlacesAutocomplete({
                        input: searchTerm,
                        country: selectedAddressCountry,
                        sessiontoken: sessionToken,
                        language: 'en'
                    });
                    const predictions = response.data?.predictions ?? [];
                    setAddressOptions(predictions.map((prediction) => ({
                        label: prediction.description,
                        value: prediction.place_id
                    })));
                }
            }
            catch {
                setAddressOptions([]);
            }
            finally {
                setIsAddressSuggestionsLoading(false);
            }
        }, ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS);
        return () => window.clearTimeout(timeout);
    }, [addressInput, isRegistered, sameAddress, selectedAddressCountry, sessionToken]);
    useEffect(() => {
        if (!setValue)
            return;
        if (travelMode === 'bus' && transportLocation && locationHasAccommodation) {
            if (normalized(p.accommodationRequired) !== 'true') {
                setValue(personField(index, 'accommodationRequired'), 'true', {
                    shouldDirty: true,
                    shouldValidate: true
                });
            }
            return;
        }
        if (String(p.accommodationRequired ?? '').trim().length > 0) {
            setValue(personField(index, 'accommodationRequired'), '', {
                shouldDirty: true,
                shouldValidate: true
            });
        }
    }, [
        travelMode,
        transportLocation,
        locationHasAccommodation,
        p.accommodationRequired,
        index,
        setValue
    ]);
    useEffect(() => {
        if (!setValue || showHotelBookingQuestion)
            return;
        setValue(personField(index, 'bookingInfoRequired'), '', {
            shouldValidate: true,
            shouldDirty: true
        });
        setValue(personField(index, 'bookingInfo'), '', {
            shouldValidate: true,
            shouldDirty: true
        });
    }, [index, setValue, showHotelBookingQuestion]);
    const fetchCountryDetails = (country, _formattedValue) => {
        const details = (country ?? {});
        setValue?.(f('PhoneCountryCode'), String(details.countryCode ?? ''), {
            shouldDirty: true,
            shouldValidate: true
        });
        setValue?.(f('PhoneDialCode'), String(details.dialCode ?? ''), {
            shouldDirty: true,
            shouldValidate: true
        });
    };
    return (_jsxs(Box, { sx: {
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
            backgroundColor: theme.palette.background.paper,
            p: 2.5
        }, children: [showHeader && (_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsx(Typography, { variant: 'subtitle1', fontWeight: 500, color: 'text.primary', children: t('familyForm.person') || 'Person' }), _jsx(Box, { borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.primary.main, width: 24, height: 24, children: _jsx(Typography, { fontSize: '10.67px', fontFamily: 'SuisseIntl', color: theme.palette.common.white, lineHeight: '160%', fontWeight: 500, children: displayNumber ?? index + 1 }) })] }), _jsx(IconButton, { onClick: () => totalCount > 1 && onRemove(index), disabled: totalCount === 1, sx: {
                            width: 36,
                            height: 32,
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
                            borderRadius: 1
                        }, children: _jsx(Icon, { icon: 'eva:close-outline', width: 16, height: 16 }) })] })), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('firstName'), label: t('familyForm.firstName') || 'First Name', bgColor: bgColor, rules: {
                                required: t('familyForm.fieldRequired') || 'This field is required'
                            }, error: fieldError('firstName') }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('middleName'), label: t('familyForm.middleName') || 'Middle Name (Optional)', bgColor: bgColor }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('lastName'), label: t('familyForm.lastName') || 'Last Name', bgColor: bgColor, rules: {
                                required: t('familyForm.fieldRequired') || 'This field is required'
                            }, error: fieldError('lastName') }) }), !hideCompany && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(EAutocomplete, { control: control, name: f('company'), label: t('familyForm.companyName') || 'Company', options: companyFieldOptions, multiple: false, bgColor: bgColor, rules: {
                                required: !isAdditionalPerson
                                    ? t('familyForm.fieldRequired') || 'This field is required'
                                    : false
                            }, error: fieldError('company') }) })), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('email'), label: t('familyForm.email') || 'Email', bgColor: bgColor, fieldType: '2', rules: {
                                required: !isAdditionalPerson
                                    ? t('familyForm.fieldRequired') || 'This field is required'
                                    : false,
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: t('familyForm.invalidEmail') || 'Invalid email'
                                }
                            }, error: fieldError('email') }) }), (showStepTwoFields || isAdditionalPerson) && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ESelect, { control: control, name: f('relationshipToYou'), label: t('familyForm.relationshipToYou') || 'Relationship to you', options: relationOptions, bgColor: bgColor, rules: {
                                required: t('familyForm.fieldRequired') || 'This field is required'
                            }, error: fieldError('relationshipToYou'), disabled: effectiveComingWith === 'guest' || effectiveComingWith === 'introduction' }) })), !showStepTwoFields && !isAdditionalPerson && (_jsx(_Fragment, { children: _jsx(Grid, { size: { xs: 12 }, children: _jsx(EButtonGroupField, { control: control, name: f('eventChoice'), label: t('familyForm.comingToEvent') || 'Coming to the event?', bgColor: bgColor, defaultValue: '', options: attendanceDecisionOptions, disabled: isRegistered, rules: {
                                    required: t('familyForm.fieldRequired') || 'This field is required'
                                }, error: fieldError('eventChoice'), onChange: (val) => {
                                    const isComing = val === 'true';
                                    setValue(f('attendanceStatus'), isComing ? String(FamilyFestAttendanceStatusEnum.ComingTo) : '', {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                    setValue(f('reasonForCancellation'), '', {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                }, children: isNotAttending && (_jsxs(Box, { sx: {
                                        mt: 2,
                                        pt: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }, children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', children: t('familyForm.notAttendingReason') ||
                                                'Please select a reason for not coming to the event.' }), _jsx(Controller, { name: f('attendanceStatus'), control: control, defaultValue: attendanceStatus === String(FamilyFestAttendanceStatusEnum.ComingTo)
                                                ? ''
                                                : String(attendanceStatus ?? ''), rules: {
                                                required: t('familyForm.fieldRequired') || 'This field is required'
                                            }, render: ({ field }) => (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }, children: notAttendingOptions.map((option) => {
                                                    const isSelected = field.value === option.value;
                                                    return (_jsx(Button, { variant: 'outlined', color: 'success', fullWidth: true, disabled: isRegistered, sx: {
                                                            fontSize: '11px',
                                                            backgroundColor: isSelected ? '#4D850C' : '#FFFFFF',
                                                            color: isSelected ? '#FFFFFF' : '#4D850C',
                                                            border: '1.5px solid #4D850C',
                                                            borderRadius: '12px',
                                                            fontWeight: 500,
                                                            '&:hover': {
                                                                backgroundColor: isSelected ? '#4D850C' : '#FFFFFF',
                                                                color: isSelected ? '#FFFFFF' : '#4D850C'
                                                            }
                                                        }, onClick: () => {
                                                            field.onChange(option.value);
                                                            setValue(f('eventChoice'), 'false', {
                                                                shouldValidate: true,
                                                                shouldDirty: true
                                                            });
                                                            if (option.value !== String(FamilyFestAttendanceStatusEnum.Others)) {
                                                                setValue(f('reasonForCancellation'), '', {
                                                                    shouldValidate: true,
                                                                    shouldDirty: true
                                                                });
                                                            }
                                                        }, disableRipple: true, children: option.label }, option.value));
                                                }) })) }), _jsx(ErrorWithIcon, { error: fieldError('attendanceStatus'), mt: 0.5 })] })) }, `attendance-decision-${index}`) }) })), normalized(attendanceStatus) === String(FamilyFestAttendanceStatusEnum.Others) && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('reasonForCancellation'), label: t('familyForm.reasonForCancellation') || 'Reason for cancellation (Optional)', bgColor: bgColor, disabled: isRegistered }) })), effectiveEventChoice === 'true' && (_jsxs(_Fragment, { children: [isAdditionalPerson && index > 0 && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ESwitch, { control: control, name: f('sameAddress'), bgColor: bgColor, label: t('familyForm.sameAddressAsPrimary', {
                                        name: [persons?.[0]?.firstName, persons?.[0]?.middleName, persons?.[0]?.lastName]
                                            .filter((x) => String(x ?? '').trim().length > 0)
                                            .join(' ') || (t('familyForm.person') || 'Person') + ' 1'
                                    }) || 'Same address as primary person', disabled: isRegistered }) })), !sameAddress && (_jsxs(_Fragment, { children: [_jsxs(Grid, { size: { xs: 12 }, children: [_jsxs(Box, { position: 'relative', width: '100%', children: [_jsx(Typography, { sx: {
                                                            position: 'absolute',
                                                            top: 6,
                                                            left: 14,
                                                            zIndex: 1,
                                                            pointerEvents: 'none'
                                                        }, variant: 'caption', color: 'text.secondary', children: t('familyForm.travelingFrom') || 'Traveling from' }), _jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: (e) => setFlagAnchorEl(e.currentTarget), disabled: isRegistered, "aria-label": t('familyForm.travelingFrom') || 'Traveling from', sx: {
                                                            height: '56px',
                                                            border: `1px solid ${flagOpen ? theme.palette.primary.main : alpha(theme.palette.grey[500], 0.2)}`,
                                                            backgroundColor: bgColor,
                                                            px: 1.5,
                                                            pr: '13px',
                                                            pt: '26px',
                                                            pb: '6px'
                                                        }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 0.5, pb: 0.5, children: [_jsxs(Box, { display: 'flex', gap: 0.5, alignItems: 'center', children: [_jsx("img", { src: currentFlag.icon, alt: currentFlag.code, style: { width: 18, height: 18, borderRadius: '2px' } }), _jsx(Typography, { variant: 'body2', color: 'text.primary', children: currentFlag.label })] }), _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', width: 24, height: 24, color: '#575756', style: {
                                                                        transform: flagOpen ? 'rotate(180deg)' : 'none',
                                                                        transition: 'transform 0.2s'
                                                                    } })] }) })] }), _jsx(Menu, { anchorEl: flagAnchorEl, open: flagOpen, onClose: () => setFlagAnchorEl(null), anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, PaperProps: {
                                                    sx: {
                                                        minWidth: flagAnchorEl?.clientWidth
                                                    }
                                                }, children: ADDRESS_FLAGS.map((flag) => (_jsx(MenuItem, { selected: flag.code === currentFlag.code, disabled: flag.disabled, onClick: () => {
                                                        if (flag.disabled || !setValue)
                                                            return;
                                                        if (setValue) {
                                                            setValue(f('addressCountry'), flag.code, {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('addressPlaceId'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('addressLatitude'), null, {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('addressLongitude'), null, {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('Address'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('postalcode'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('houseNumber'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('addition'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: true
                                                            });
                                                            setValue(f('Phone'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: false
                                                            });
                                                            setValue(f('PhoneCountryCode'), flag.code, {
                                                                shouldDirty: true,
                                                                shouldValidate: false
                                                            });
                                                            setValue(f('PhoneDialCode'), '', {
                                                                shouldDirty: true,
                                                                shouldValidate: false
                                                            });
                                                        }
                                                        setAddressInput('');
                                                        setAddressOptions([]);
                                                        setPostalCodeInput('');
                                                        setPostalCodeOptions([]);
                                                        setSessionToken(uuidv4());
                                                        setFlagAnchorEl(null);
                                                    }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', children: [_jsx("img", { src: flag.icon, alt: flag.code, style: { width: 20, height: 20, marginRight: 8 } }), _jsx(Typography, { variant: 'body2', children: flag.label })] }) }, flag.code))) })] }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Autocomplete, { options: addressOptions, value: p.addressPlaceId && p.Address
                                                ? { label: String(p.Address), value: String(p.addressPlaceId) }
                                                : null, inputValue: addressInput, disabled: isRegistered, onInputChange: (_, value, reason) => {
                                                setAddressInput(value);
                                                if (!setValue)
                                                    return;
                                                if (reason === 'input') {
                                                    setValue(f('addressPlaceId'), '', {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('addressLatitude'), null, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('addressLongitude'), null, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('Address'), '', { shouldDirty: true, shouldValidate: true });
                                                }
                                                if (reason === 'clear') {
                                                    setValue(f('addressPlaceId'), '', {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('addressLatitude'), null, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('addressLongitude'), null, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('Address'), '', { shouldDirty: true, shouldValidate: true });
                                                }
                                            }, onChange: async (_, newValue) => {
                                                if (!setValue)
                                                    return;
                                                setValue(f('addressPlaceId'), '', {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue(f('addressLatitude'), null, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue(f('addressLongitude'), null, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue(f('Address'), '', { shouldDirty: true, shouldValidate: true });
                                                if (!newValue?.value)
                                                    return;
                                                try {
                                                    setIsPlaceDetailsLoading(true);
                                                    let placeId;
                                                    let formattedAddress;
                                                    let latitude;
                                                    let longitude;
                                                    let postalCode = '';
                                                    if (selectedAddressCountry === 'nl') {
                                                        const detail = await pdokLookup(newValue.value);
                                                        const coords = parsePdokCoordinates(detail?.centroide_ll ?? '');
                                                        placeId = detail?.id ?? newValue.value;
                                                        formattedAddress = detail?.weergavenaam ?? newValue.label;
                                                        latitude = coords.lat;
                                                        longitude = coords.lng;
                                                        postalCode = detail?.postcode ?? '';
                                                    }
                                                    else {
                                                        const detailsResponse = await pickupLocationAPI.getPlaceDetails({
                                                            placeId: newValue.value,
                                                            sessiontoken: sessionToken,
                                                            language: 'en'
                                                        });
                                                        const result = detailsResponse.data?.result;
                                                        placeId = result?.place_id ?? newValue.value;
                                                        formattedAddress = result?.formatted_address ?? newValue.label;
                                                        latitude = result?.geometry?.location?.lat ?? null;
                                                        longitude = result?.geometry?.location?.lng ?? null;
                                                    }
                                                    setValue(f('addressPlaceId'), placeId, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('Address'), formattedAddress, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('addressLatitude'), latitude, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setValue(f('addressLongitude'), longitude, {
                                                        shouldDirty: true,
                                                        shouldValidate: true
                                                    });
                                                    setAddressInput(formattedAddress);
                                                    if (postalCode) {
                                                        setValue(f('postalcode'), postalCode, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                        setPostalCodeInput(postalCode);
                                                    }
                                                }
                                                finally {
                                                    setIsPlaceDetailsLoading(false);
                                                }
                                            }, getOptionLabel: (option) => option?.label ?? '', isOptionEqualToValue: (option, value) => option.value === value.value, loading: isAddressSuggestionsLoading, noOptionsText: addressInput.trim().length < ADDRESS_AUTOCOMPLETE_MIN_LENGTH
                                                ? t('events.eventDetails.transportTab.startTypingForSuggestions') ||
                                                    'Start typing for suggestions'
                                                : t('labels.noOptions') || 'No options', popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill' }), sx: {
                                                '& .MuiFilledInput-root': {
                                                    backgroundColor: `${bgColor} !important`,
                                                    borderRadius: '12px !important'
                                                }
                                            }, renderInput: (params) => (_jsx(TextField, { ...params, label: t('familyForm.homeAddress') || 'Home Address', placeholder: t('familyForm.searchAddressPlaceholder') ||
                                                    'Search by street, houseno, postal code, or city', variant: 'filled', error: !!fieldError('addressPlaceId'), helperText: fieldError('addressPlaceId'), slotProps: {
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (_jsxs(_Fragment, { children: [(isAddressSuggestionsLoading || isPlaceDetailsLoading) && (_jsx(CircularProgress, { color: 'inherit', size: 20 })), params.InputProps.endAdornment] }))
                                                    }
                                                } })) }) }), selectedAddressCountry === 'nl' && (_jsxs(Grid, { size: { xs: 12 }, children: [_jsx(Autocomplete, { options: postalCodeOptions, value: p.postalcode
                                                    ? { label: String(p.postalcode), value: String(p.postalcode) }
                                                    : null, inputValue: postalCodeInput, disabled: isRegistered, onInputChange: (_, value, reason) => {
                                                    setPostalCodeInput(value);
                                                    if (reason === 'input' && setValue) {
                                                        setValue(f('postalcode'), value, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                    }
                                                    if (reason === 'clear' && setValue) {
                                                        setValue(f('postalcode'), '', {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                    }
                                                }, onChange: async (_, newValue) => {
                                                    if (!setValue || typeof newValue !== 'object' || !newValue?.value)
                                                        return;
                                                    try {
                                                        setIsPostalCodeDetailsLoading(true);
                                                        let placeId;
                                                        let formattedAddress;
                                                        let latitude;
                                                        let longitude;
                                                        let postalCode = '';
                                                        if (selectedAddressCountry === 'nl') {
                                                            const detail = await pdokLookup(newValue.value);
                                                            const coords = parsePdokCoordinates(detail?.centroide_ll ?? '');
                                                            placeId = detail?.id ?? newValue.value;
                                                            formattedAddress = detail?.weergavenaam ?? newValue.label;
                                                            latitude = coords.lat;
                                                            longitude = coords.lng;
                                                            postalCode = detail?.postcode ?? '';
                                                        }
                                                        else {
                                                            const detailsResponse = await pickupLocationAPI.getPlaceDetails({
                                                                placeId: newValue.value,
                                                                sessiontoken: sessionToken,
                                                                language: 'en'
                                                            });
                                                            const result = detailsResponse.data?.result;
                                                            placeId = result?.place_id ?? newValue.value;
                                                            formattedAddress = result?.formatted_address ?? newValue.label;
                                                            latitude = result?.geometry?.location?.lat ?? null;
                                                            longitude = result?.geometry?.location?.lng ?? null;
                                                        }
                                                        setValue(f('postalcode'), postalCode || postalCodeInput, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                        setValue(f('addressPlaceId'), placeId, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                        setValue(f('Address'), formattedAddress, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                        setValue(f('addressLatitude'), latitude, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                        setValue(f('addressLongitude'), longitude, {
                                                            shouldDirty: true,
                                                            shouldValidate: true
                                                        });
                                                        setPostalCodeInput(postalCode || postalCodeInput);
                                                        setAddressInput(formattedAddress);
                                                        setPostalCodeOptions([]);
                                                    }
                                                    finally {
                                                        setIsPostalCodeDetailsLoading(false);
                                                    }
                                                }, getOptionLabel: (option) => typeof option === 'string' ? option : (option?.label ?? ''), isOptionEqualToValue: (option, value) => typeof option === 'string' || typeof value === 'string'
                                                    ? option === value
                                                    : option.value === value.value, loading: isPostalCodeSuggestionsLoading, noOptionsText: postalCodeInput.trim().length < ADDRESS_AUTOCOMPLETE_MIN_LENGTH
                                                    ? t('events.eventDetails.transportTab.startTypingForSuggestions') ||
                                                        'Start typing for suggestions'
                                                    : t('labels.noOptions') || 'No options', freeSolo: true, popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill' }), sx: {
                                                    '& .MuiFilledInput-root': {
                                                        backgroundColor: `${bgColor} !important`,
                                                        borderRadius: '12px !important'
                                                    }
                                                }, renderInput: (params) => (_jsx(TextField, { ...params, label: t('familyForm.postalCode') || 'Postal Code', variant: 'filled', error: !!fieldError('postalcode'), helperText: fieldError('postalcode'), slotProps: {
                                                        input: {
                                                            ...params.InputProps,
                                                            endAdornment: (_jsxs(_Fragment, { children: [isPostalCodeDetailsLoading && (_jsx(CircularProgress, { color: 'inherit', size: 20 })), params.InputProps.endAdornment] }))
                                                        }
                                                    } })) }), _jsx(Box, { sx: { display: 'none' }, children: _jsx(ETextField, { control: control, name: f('postalcode'), label: '', bgColor: bgColor, rules: {
                                                        validate: (value) => String(value ?? '').trim()
                                                            ? true
                                                            : t('familyForm.fieldRequired') || 'This field is required'
                                                    }, error: fieldError('postalcode') }) })] }))] })), sameAddress && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('Address'), label: t('familyForm.homeAddress') || 'Home Address', bgColor: bgColor, disabled: true }) })), sameAddress && selectedAddressCountry === 'nl' && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('postalcode'), label: t('familyForm.postalCode') || 'Postal Code', bgColor: bgColor, disabled: true }) })), _jsx(Grid, { size: { xs: 12 }, sx: { display: 'none' }, children: _jsx(ETextField, { control: control, name: f('addressPlaceId'), label: '', bgColor: bgColor, rules: {
                                        validate: (value) => sameAddress || String(value ?? '').trim() || String(p.Address ?? '').trim()
                                            ? true
                                            : t('familyForm.fieldRequired') || 'This field is required'
                                    }, error: fieldError('addressPlaceId') }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(PhoneInputMasking, { control: control, label: t('familyForm.phoneNumber') || 'Phone Number', name: f('Phone'), bg: 'grey8', disabled: isRegistered, fetchCountryDetails: fetchCountryDetails, countryCode: p.PhoneCountryCode || selectedAddressCountry, rules: {
                                        required: !isAdditionalPerson
                                            ? t('familyForm.fieldRequired') || 'This field is required'
                                            : false,
                                        validate: {
                                            phoneFormat: (value, formValues) => {
                                                if (!value)
                                                    return true;
                                                const person = formValues?.persons?.[index];
                                                return (isPhoneValidForCountry(value, person?.PhoneDialCode, person?.PhoneCountryCode) ||
                                                    t('validations.invalidPhone', { field: t('familyForm.phoneNumber') }) ||
                                                    'Invalid phone number');
                                            }
                                        }
                                    }, error: fieldError('Phone') }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(EDatePicker, { control: control, name: f('DateOfBirth'), label: t('familyForm.dateOfBirth') || 'Date of Birth', bgColor: bgColor, selectedTone: 'darker', maxDate: isSwedenCountry ? dayjs().subtract(14, 'year') : dayjs(), rules: {
                                        required: t('familyForm.fieldRequired') || 'This field is required',
                                        validate: (v) => {
                                            if (v == null || v === '')
                                                return true;
                                            const d = dayjs(v);
                                            if (!d.isValid())
                                                return true;
                                            if (d.isAfter(dayjs(), 'day'))
                                                return (t('familyForm.dobNotFuture') || 'Date of birth cannot be later than today');
                                            if (isSwedenCountry && d.isAfter(dayjs().subtract(14, 'year'), 'day'))
                                                return (t('familyForm.attendeesUnder14NotPermitted') ||
                                                    'Attendees under the age of 14 are not permitted to attend this event.');
                                            return true;
                                        }
                                    }, disabled: isRegistered, error: fieldError('DateOfBirth') }) }), isSwedenCountry && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(Alert, { severity: 'info', sx: { borderRadius: '8px' }, children: t('familyForm.attendeesUnder14NotPermitted', {
                                        defaultValue: 'Attendees under the age of 14 are not permitted to attend this event.'
                                    }) }) })), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ERadioGroup, { control: control, name: f('TravelMode'), label: t('familyForm.travelMode') || 'How are you traveling?', bgColor: bgColor, options: [
                                        { label: t('familyForm.car') || 'Car', value: 'auto' },
                                        isSwedenCountry || travelMode === 'plane'
                                            ? { label: t('familyForm.plane') || 'Plane', value: 'plane' }
                                            : { label: t('familyForm.bus') || 'Bus', value: 'bus' }
                                    ] }) }), travelMode === 'plane' && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(Alert, { severity: 'info', sx: { borderRadius: 1.5 }, children: t('familyForm.passportInfoInPassportTab') ||
                                                'Passport information for this person can be seen in the Passport tab only.' }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { sx: {
                                                borderRadius: '12px',
                                                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                                backgroundColor: theme.palette.background.default,
                                                p: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1.5
                                            }, children: [_jsx(Typography, { color: 'text.primary', fontSize: 14, children: t('familyForm.planeTransportOverviewDescription') ||
                                                        'Select your preferred airport.' }), _jsx(EAutocomplete, { control: control, name: f('transportLocation'), label: t('familyForm.selectPreferredAirport') || 'Select airport', options: airportOptions, multiple: false, bgColor: bgColor, rules: {
                                                        required: t('familyForm.fieldRequired') || 'This field is required'
                                                    }, error: fieldError('transportLocation') })] }) })] })), travelMode === 'bus' && !isSwedenCountry && (_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { sx: {
                                        borderRadius: '12px',
                                        border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                        backgroundColor: theme.palette.background.default,
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5
                                    }, children: [_jsx(Typography, { color: 'text.primary', fontSize: 14, children: t('familyForm.transportOverviewDescription') ||
                                                'Select your preferred pickup location.' }), _jsx(EAutocomplete, { control: control, name: f('transportLocation'), label: t('familyForm.selectLocation') || 'Select location', options: transportFieldOptions, multiple: false, bgColor: bgColor, disablePortal: true, listboxMaxHeight: 360, getOptionLabel: (option) => getPickupLocationInputLabel(option), filterOptions: (opts, state) => filterPickupLocationOptions(opts, state.inputValue), renderOption: (props, option) => {
                                                const { key, ...optionProps } = props;
                                                return (_jsx(Box, { component: 'li', ...optionProps, sx: { alignItems: 'stretch !important' }, children: _jsx(PickupLocationOptionRow, { option: option }) }, key));
                                            }, rules: {
                                                required: t('familyForm.fieldRequired') || 'This field is required'
                                            }, error: fieldError('transportLocation') }), transportLocation && locationHasAccommodation && (_jsx(Alert, { severity: 'info', sx: { borderRadius: 1.5 }, children: t('familyForm.accommodationProvidedForSelectedLocation') ||
                                                'Accommodation will be provided for the selected location.' }))] }) })), _jsx(Grid, { size: { xs: 12 }, children: _jsx(EButtonGroupField, { control: control, name: f('Option'), label: isAdditionalPerson && personName
                                        ? t('familyForm.wheelchairDependentPerson', { name: personName }) ||
                                            `Is ${personName} wheelchair dependent?`
                                        : t('familyForm.wheelchairDependent') || 'Are you wheelchair dependent?', bgColor: bgColor, defaultValue: '', options: [
                                        { label: t('familyForm.yes') || 'Yes', value: 'true' },
                                        { label: t('familyForm.no') || 'No', value: 'false' }
                                    ], disabled: isRegistered }) }), selectedOption === 'true' && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('supportComments'), label: t('familyForm.supportComments') || 'Support comments (Optional)', bgColor: bgColor, disabled: isRegistered }) })), showHotelBookingQuestion && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(EButtonGroupField, { control: control, name: f('bookingInfoRequired'), label: (isAdditionalPerson && personName.trim()
                                        ? t('familyForm.hotelBookingQuestionPerson', { name: personName })
                                        : t('familyForm.hotelBookingQuestion')), bgColor: bgColor, defaultValue: '', options: [
                                        { label: t('familyForm.yes') || 'Yes', value: 'true' },
                                        { label: t('familyForm.no') || 'No', value: 'false' }
                                    ] }) })), showHotelBookingQuestion && bookingInfoChoice === 'true' && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(ETextField, { control: control, name: f('bookingInfo'), label: t('familyForm.bookingInfo') || 'Booking info (Optional)', bgColor: bgColor }) })), !showStepTwoFields && !isAdditionalPerson && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(EButtonGroupField, { control: control, name: f('ComingWith'), label: t('familyForm.comingWith') || 'Coming with', bgColor: bgColor, defaultValue: '', options: [
                                        { label: t('familyForm.comingWithFamily') || 'Family', value: 'family' },
                                        {
                                            label: t('familyForm.comingWithGuest') || 'Guest',
                                            value: 'guest'
                                        },
                                        { label: t('familyForm.comingWithAlone') || 'Alone', value: 'alone' }
                                    ], disabled: isRegistered }) }))] }))] })] }));
};
export default FamilyPersonCard;
